import React from 'react'
import { Block } from 'slate'
import Element from './Element'
import { getNodeAsText } from 'utils/helpers'

const baseChildNodes = [
  {
    match: {
      type: 'elementLabel'
    },
    min: 1,
    max: 1
  },
  {
    match: {
      type: 'elementTitle'
    },
    min: 1,
    max: 1
  }
]

const baseNormalizer = (editor, { code, node, child, index }) => {
  switch (code) {
    case 'child_min_invalid':
      const hasElementLabel = node.nodes.find(n => n.type === 'elementLabel')
      const hasElementTitle = node.nodes.find(n => n.type === 'elementTitle')

      if (!hasElementLabel) {
        editor.insertNodeByKey(
          node.key,
          0,
          createElementLabel('Please replace this with a question label...')
        )
      }

      if (!hasElementTitle) {
        editor.insertNodeByKey(
          node.key,
          1,
          createElementTitle('Please replace this with a question title...')
        )
      }
      break
    default:
      break
  }
}

const createElement = (type, lines, childTypes, data = {}) => {
  return Block.create({
    type: type,
    nodes: lines.map((line, ix) => {
      return Block.create({
        type:
          ix >= childTypes.length
            ? childTypes[childTypes.length - 1]
            : childTypes[ix],
        data: data,
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                object: 'leaf',
                text: line
              }
            ]
          }
        ]
      })
    })
  })
}

const createElementBlocks = (
  editor,
  lineGroup,
  originalParentType,
  parentType,
  childTypes
) => {
  let blocks = []
  const lineGroupLines = getNodeAsText(lineGroup).split('\n')
  const currentLabelText =
    originalParentType !== 'lineGroup' ? lineGroupLines.shift() : ''

  childTypes.unshift('elementLabel')
  lineGroupLines.unshift(editor.getNextLabelText(currentLabelText, parentType))

  if (childTypes.length) {
    blocks = lineGroupLines.map((text, ix) => {
      const childType =
        childTypes.length - 1 > ix
          ? childTypes[ix]
          : childTypes[childTypes.length - 1]

      if (childType === 'elementLabel') {
        return createElementLabel(text)
      }

      if (childType.toLowerCase().includes('answer')) {
        return createAnswer(childType, text)
      }

      return createElementChild(childType, text)
    })
  }

  return blocks
}

const createElementChild = (type, text) => {
  return Block.create({
    object: 'block',
    type: type,
    nodes: [
      {
        object: 'text',
        leaves: [
          {
            marks: [],
            object: 'leaf',
            text: text
          }
        ]
      }
    ]
  })
}

const createElementLabel = text => createElementChild('elementLabel', text)
const createElementTitle = text => createElementChild('elementTitle', text)
const createAnswer = (type, text) =>
  createElementChild(type, text.replace(/^[\Wo]\s/, ''))

export const convertToElement = (
  editor,
  node,
  type,
  childTypes,
  userModified = true
) => {
  if (node.type === type) {
    return
  }

  const originalType = node.type
  if (node.type !== 'lineGroup') {
    const childrenText = node.nodes
      .map(node => node.getFirstText().text)
      .join('\n')
    const lineGroup = createElementChild('lineGroup', childrenText)

    editor.replaceNodeByKey(node.key, lineGroup)
    node = lineGroup
  }

  const generateNewLabel = node.type === originalType
  const childBlocks = createElementBlocks(
    editor,
    node,
    originalType,
    type,
    childTypes,
    generateNewLabel
  )

  const questionBlock = Block.create({
    type: type,
    nodes: childBlocks,
    data: {
      userModified: userModified
    }
  })
  editor.replaceNodeByKey(node.key, questionBlock)
}

const getElementLabel = node =>
  node.findDescendant(node => node.type === 'elementLabel')

const destructureElement = (editor, node, userModified = true) => {
  if (editor.isNodeType(node, 'lineGroup')) return

  const text = getNodeAsText(node)
  const nodeData = node.data.toJSON()

  nodeData.userModified = userModified
  editor.replaceNodeByKey(
    node.key,
    Block.create({
      type: 'lineGroup',
      data: nodeData,
      nodes: [
        {
          object: 'text',
          leaves: [
            {
              object: 'leaf',
              text: text
            }
          ]
        }
      ]
    })
  )
}

const removeAdjacentElement = (editor, predicate, after = false) => {
  const adjacentElement = after
    ? editor.getNextElement()
    : editor.getPreviousElement()

  if (!adjacentElement) return

  if (predicate) {
    predicate(adjacentElement) && editor.removeNodeByKey(adjacentElement.key)
  } else {
    editor.removeNodeByKey(adjacentElement.key)
  }
}

const moveToAdjacentElement = (editor, after = false) => {
  const adjacentElement = after
    ? editor.getNextElement()
    : editor.getPreviousElement()

  if (!adjacentElement) return

  const child = after
    ? adjacentElement.getFirstText()
    : adjacentElement.getLastText()
  const offset = after ? 0 : child.text.length

  editor.moveTo(child.key, offset)
}

const cleanAdjacentElement = (editor, after = false) => {
  const adjacentElement = after
    ? editor.getNextElement()
    : editor.getPreviousElement()
  if (!adjacentElement) return

  adjacentElement.nodes
    .filter(node => !node.text.trim())
    .forEach(node => editor.removeNodeByKey(node.key))
}

const ElementPlugin = options => {
  return {
    onKeyDown(ev, editor, next) {
      switch (ev.key) {
        case 'Enter':
          // This allows the user to breakout of an element with double enter
          if (editor.isTextBlank()) {
            editor
              .insertLineGroupAfter(editor.getElement())
              .moveToNextElement()
              .cleanPreviousElement()
              .predictSurveyElement(editor.getPreviousElement())
            return
          }
          return next()
        case 'Backspace':
          const currentNode = editor.getElement()
          const children = currentNode.nodes.filter(
            node => node.type === 'elementLabel' || node.text.trim()
          )

          if (!children.size) {
            const prevNode = editor.getPreviousElement()

            editor.destructureElement(currentNode)

            if (editor.isNodeType(prevNode, 'pageBreak')) {
              editor.removeNodeByKey(prevNode.key)
            }
          }

          return next()
        default:
          break
      }
      return next()
    },
    renderNode(props, editor, next) {
      switch (props.node.type) {
        case 'element':
          return <Element {...props} />
        case 'elementLabel':
          return <Element.Label {...props} />
        case 'elementTitle':
          return <Element.Title {...props} />
        default:
          return next()
      }
    },
    commands: {
      registerElement(editor, type, options) {
        const { menu } = options
        editor
          .registerHoverMenuButton(options.menu.command, menu)
          .registerLabelPrefix(type, options.labelPrefix)
          .registerSerializer(type, options.childTypes)
      },
      convertToElement(editor, node, type, childTypes, userModified = true) {
        convertToElement(
          editor,
          node,
          type,
          childTypes.map(child => child),
          userModified
        )
      },
      destructureElement(editor, node) {
        destructureElement(editor, node)
      },
      pruneUnstructured(editor) {
        const { document: doc } = editor.value

        doc.nodes.forEach(n => {
          if (n.type === 'lineGroup') {
            editor.removeNodeByKey(n.key)
          }
        })
      },
      removePreviousElement(editor, predicate) {
        removeAdjacentElement(editor, predicate)
      },
      removeNextElement(editor, predicate) {
        removeAdjacentElement(editor, predicate, true)
      },
      moveToPreviousElement(editor) {
        moveToAdjacentElement(editor)
      },
      moveToNextElement(editor) {
        moveToAdjacentElement(editor, true)
      },
      cleanPreviousElement(editor) {
        cleanAdjacentElement(editor)
      },
      cleanNextElement(editor) {
        cleanAdjacentElement(editor, true)
      }
    },
    queries: {
      getElement(editor) {
        const { document: doc, selection } = editor.value

        return doc.getFurthestBlock(doc.getNode(selection.focus.key).key)
      },
      getPreviousElement(editor) {
        const parent = editor.getElement()

        return editor.value.document.getPreviousSibling(parent.key)
      },
      getNextElement(editor) {
        const parent = editor.getElement()

        return editor.value.document.getNextSibling(parent.key)
      },
      isElementTextBlank(editor) {
        return !editor.getElement().text
      },
      isElementTrimmedTextBlank(editor) {
        return !editor.getElement().text.trim()
      },
      isTextBlank(editor) {
        const { document: doc, selection } = editor.value

        return !doc.getNode(selection.focus.key).text
      },
      isTrimmedTextBlank(editor) {
        const { document: doc, selection } = editor.value

        return !doc.getNode(selection.focus.key).text.trim()
      }
    }
  }
}

export {
  baseChildNodes,
  baseNormalizer,
  Element,
  getElementLabel,
  createElement,
  createElementChild,
  createElementLabel
}
export default ElementPlugin



// WEBPACK FOOTER //
// ./src/components/Editor/plugins/ElementPlugin/index.js